import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button, ErrorText, Flex, FormField, Modal, TextField } from '@cmaas/ui';
import type { Tenant } from '../../api/types';
import { apiFetch, CmaasApiError } from '../../api/client';

// The tenant name is the Apple UAP issuer organization, which Apple caps at 64 characters.
const createSchema = z.object({ name: z.string().trim().min(1).max(64) });
type CreateValues = z.infer<typeof createSchema>;

const mutationError = (err: unknown) => (err instanceof CmaasApiError ? err.message : 'Something went wrong.');

export function NewTenantModal({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const queryClient = useQueryClient();
  const key = ['tenants'];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateValues>({ resolver: zodResolver(createSchema) });

  const create = useMutation({
    mutationFn: (values: CreateValues) => apiFetch<Tenant>('/v1/admin/tenants', { method: 'POST', body: values }),
    onSuccess: () => {
      reset();
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: ['tenant-options'] });
      onOpenChange(false);
    },
  });

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      reset();
      create.reset();
    }
    onOpenChange(next);
  };

  return (
    <Modal open={open} onOpenChange={handleOpenChange} title="New tenant">
      <form onSubmit={handleSubmit((v) => create.mutate(v))}>
        <Flex direction="column" gap="3" mt="4">
          <FormField label="Name" error={errors.name?.message}>
            <TextField.Root {...register('name')} />
          </FormField>
          {create.isError && <ErrorText>{mutationError(create.error)}</ErrorText>}
          <Flex gap="3" justify="end" mt="2">
            <Button type="button" variant="soft" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              Create tenant
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
}
